import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
  HttpEventType,
  HttpSentEvent,
  HttpProgressEvent,
  HttpParameterCodec,
} from '@angular/common/http';
import _merge from 'lodash-es/merge';
import _isArray from 'lodash-es/isArray';
import _isObject from 'lodash-es/isObject';
import { Observable, forkJoin } from 'rxjs';
import { filter, map, retry, tap } from 'rxjs/operators';
import { BaseCacheService } from './base-cache.service';

export type TRequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type TResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';
export type TApiBatchRequestSplitType = 'length' | 'count';

export interface IApiUploadHandlers {
  loadstart?: (event: HttpSentEvent) => void;
  progress?: (event: HttpProgressEvent) => void;
  error?: (event: HttpErrorResponse) => void;
  // NOTE: this handler is never called (dunno how to identify `abort` event)
  abort?: (event: HttpErrorResponse) => void;
  loadend?: (event: HttpResponse<any>) => void;
}

export interface IApiRequestOptions {
  cache?: boolean;
  // NOTE: to send files pass FormData object here
  formParams?: Object | FormData;
  // query params for GET request or plain JSON for any other method
  params?: Object;
  responseType?: TResponseType;
  retry?: number;
  token?: string;
  uploadHandlers?: IApiUploadHandlers;
  withCredentials?: boolean;
  json?: boolean;
}

export interface IApiBatchRequestOptions extends IApiRequestOptions {
  batchLimit?: number;
  batchSplitType?: TApiBatchRequestSplitType;
  mergeResult?: boolean;
}

class GetQueryEncoderHelper implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

// thinking of 2048 limit
const BATCH_GET_LIMIT = 2000;

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  constructor(protected http: HttpClient, protected cacheService: BaseCacheService) {}

  delete<T>(url: string, baseRequestOptions?: IApiRequestOptions): Observable<T> {
    return this.request<T>('DELETE', url, baseRequestOptions).pipe(map(response => response.body));
  }

  get<T>(url: string, baseRequestOptions?: IApiRequestOptions): Observable<T> {
    return this.request<T>('GET', url, baseRequestOptions).pipe(map(response => response.body));
  }

  patch<T>(url: string, baseRequestOptions?: IApiRequestOptions): Observable<T> {
    return this.request<T>('PATCH', url, baseRequestOptions).pipe(map(response => response.body));
  }

  post<T>(url: string, baseRequestOptions?: IApiRequestOptions): Observable<T> {
    return this.request<T>('POST', url, baseRequestOptions).pipe(map(response => response.body));
  }

  put<T>(url: string, baseRequestOptions?: IApiRequestOptions): Observable<T> {
    return this.request<T>('PUT', url, baseRequestOptions).pipe(map(response => response.body));
  }

  request<T>(
    method: TRequestMethod,
    url: string,
    baseRequestOptions: IApiRequestOptions = {},
  ): Observable<HttpResponse<T>> {
    const requestOptions = this.getRequestOptions(method, url, baseRequestOptions);

    return (
      this.http
        .request<T>(requestOptions)
        // handle upload events
        .pipe(
          tap(
            event => {
              if (!baseRequestOptions.uploadHandlers) {
                return;
              }

              if (event.type === HttpEventType.Sent && baseRequestOptions.uploadHandlers.loadstart) {
                baseRequestOptions.uploadHandlers.loadstart(event);
              } else if (event.type === HttpEventType.UploadProgress && baseRequestOptions.uploadHandlers.progress) {
                baseRequestOptions.uploadHandlers.progress(event);
              } else if (event instanceof HttpResponse && baseRequestOptions.uploadHandlers.loadend) {
                baseRequestOptions.uploadHandlers.loadend(event);
              }
            },
            (error: HttpErrorResponse) => {
              if (!baseRequestOptions.uploadHandlers) {
                return;
              }

              if (baseRequestOptions.uploadHandlers.error) {
                baseRequestOptions.uploadHandlers.error(error);
              }
            },
          ),
          // TODO: try to remove `any` on TypeScript 2.6 update
          filter<any>(event => event instanceof HttpResponse),
          retry(baseRequestOptions.retry || 0),
        )
    );
  }

  batchGetRequest<T, U = any>(
    url: string,
    batchParam: { [key: string]: U[] },
    configObj: IApiBatchRequestOptions,
  ): Promise<T> {
    const splitType: TApiBatchRequestSplitType = configObj.batchSplitType || 'length';

    let requests: Observable<HttpResponse<T>>[];

    if (splitType === 'count') {
      requests = this.splitBatchByCount<T, U>(url, batchParam, configObj);
    } else {
      requests = this.splitBatchByLength<T, U>(url, batchParam, configObj);
    }

    return forkJoin(requests)
      .pipe(
        map(responses => responses.map(response => response.body)),
        map(responsesData =>
          configObj.mergeResult
            ? // data merged among all responses
              _merge({}, ...responsesData)
            : // data concatenated among all responses
              // eslint-disable-next-line prefer-spread
              [].concat.apply([], responsesData),
        ),
      )
      .toPromise();
  }

  invalidateResponseCache(url, params: Object = {}): void {
    const request = this.getRequestOptions('GET', url, { params });
    const cacheKey = request.urlWithParams;

    this.cacheService.remove(cacheKey);
  }

  getHttpParams(params: Object): HttpParams {
    let httpParams = new HttpParams({ encoder: new GetQueryEncoderHelper() });
    for (const key of Object.keys(params)) {
      const value: any = params[key];
      // skip empty params
      if (value === undefined || value === null) {
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach(el => {
          httpParams = httpParams.append(key + '[]', el);
        });
      } else {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return httpParams;
  }

  private encodeValue(param: string): string {
    return encodeURIComponent(param);
  }

  private getBatchParamKeyAndValues<T>(batchParam: { [key: string]: T[] }): [string, T[]] {
    let batchParamKey: string;
    for (const key in batchParam) {
      if (batchParam.hasOwnProperty(key)) {
        batchParamKey = key;
        break;
      }
    }

    return [batchParamKey, batchParam[batchParamKey]];
  }

  // NOTE: manual form params to string processing due to this issue https://github.com/angular/angular/issues/18719
  // currently HttpParams cant process array of items as `smth[]=`
  private getFormParamsString(params: Object): string {
    const paramsList: string[] = [];
    for (const valueKey of Object.keys(params)) {
      const value: any = params[valueKey];
      // skip empty params
      if (value === undefined || value === null) {
        continue;
      }

      const encodedValueKey = this.encodeValue(valueKey);

      // `{ smth: [ 1, 2 ] }` -> `smth[]=1&smth[]=2`
      if (_isArray(value)) {
        value.forEach(valueItem => {
          const encodedValueItem = this.encodeValue(valueItem);
          paramsList.push(`${encodedValueKey}[]=${encodedValueItem}`);
        });
        // `{ smth: { a: 1, b: 2 } }` -> `smth[a]=1&smth[b]=2`
      } else if (_isObject(value)) {
        for (const valueItemKey of Object.keys(value)) {
          const encodedValueItemKey = this.encodeValue(`${valueKey}[${valueItemKey}]`);
          const encodedValueItem = this.encodeValue(value[valueItemKey]);

          paramsList.push(`${encodedValueItemKey}=${encodedValueItem}`);
        }
      } else {
        const encodedValue = this.encodeValue(value);
        paramsList.push(`${encodedValueKey}=${encodedValue}`);
      }
    }
    return paramsList.join('&');
  }

  private getRequestOptions(
    method: string,
    url: string,
    baseRequestOptions: IApiRequestOptions = {},
  ): HttpRequest<any> {
    let body: Object | null = null;
    let headers = new HttpHeaders();
    let params: HttpParams;
    let reportProgress = false;

    if (baseRequestOptions.formParams) {
      if (baseRequestOptions.formParams instanceof FormData) {
        // NOTE: proper header will be set by browser
        body = baseRequestOptions.formParams;
      } else {
        if (baseRequestOptions.json) {
          headers = headers.set('Content-Type', 'application/json; charset=UTF-8');
          body = JSON.stringify(baseRequestOptions.formParams);
        } else {
          headers = headers.set('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
          body = this.getFormParamsString(baseRequestOptions.formParams);
        }
      }
    }

    if (baseRequestOptions.params) {
      if (method === 'GET') {
        params = this.getHttpParams(baseRequestOptions.params);
      } else {
        body = baseRequestOptions.params;
      }
    }

    if (baseRequestOptions.token) {
      headers = headers.set('Authorization', `Bearer ${baseRequestOptions.token}`);
    }

    if (baseRequestOptions.uploadHandlers) {
      reportProgress = true;
    }

    const withCredentials = baseRequestOptions.withCredentials;
    const responseType: TResponseType = baseRequestOptions.responseType || 'json';

    const requestOptions = new HttpRequest<any>(method, url, body, {
      headers,
      params,
      reportProgress,
      responseType,
      withCredentials,
    });

    // NOTE: uglyhack due to no `meta` field currently
    // https://github.com/angular/angular/issues/18155
    (requestOptions as any).meta = { cache: baseRequestOptions.cache };

    return requestOptions;
  }

  private splitBatchByCount<T, U = any>(
    url: string,
    batchParam: { [key: string]: U[] },
    configObj: IApiBatchRequestOptions,
  ): Observable<HttpResponse<T>>[] {
    const batchLimit = configObj.batchLimit;
    const [batchParamKey, batchParamValues] = this.getBatchParamKeyAndValues<U>(batchParam);
    const batches: U[][] = [];

    for (let i = 0, len = batchParamValues.length; i < len; i += batchLimit) {
      batches.push(batchParamValues.slice(i, i + batchLimit));
    }

    const requests = batches.map(batch => {
      return this.request<T>('GET', url, _merge(configObj, { params: { [batchParamKey]: batch.join() } }));
    });

    return requests;
  }

  private splitBatchByLength<T, U = any>(
    url: string,
    batchParam: { [key: string]: U[] },
    configObj: IApiBatchRequestOptions,
  ): Observable<HttpResponse<T>>[] {
    const batchLimit = configObj.batchLimit || BATCH_GET_LIMIT;

    const limit = batchLimit - url.length;
    const batches: U[][] = [[]];
    let currentBatch = 0;
    let currentLength = 0;
    const requests: Observable<HttpResponse<T>>[] = [];

    const [batchParamKey, batchParamValues] = this.getBatchParamKeyAndValues<U>(batchParam);

    if (batchParamValues.length) {
      // leaving only unique values
      batchParam[batchParamKey] = batchParamValues.filter((value, index, self) => {
        return self.indexOf(value) === index;
      });

      // Splitting to batches
      for (let i = 0; i < batchParamValues.length; i++) {
        if (currentLength < limit + batchParamValues[i].toString().length + 1) {
          batches[currentBatch].push(batchParamValues[i]);
          currentLength += batchParamValues[i].toString().length + 1;
        } else {
          currentBatch++;
          batches.push([]);
          batches[currentBatch].push(batchParamValues[i]);
          currentLength = batchParamValues[i].toString().length + 1;
        }
      }

      if (!configObj.params) {
        configObj.params = {};
      }

      for (let i = 0; i < batches.length; i++) {
        const request = this.request<T>(
          'GET',
          url,
          _merge(configObj, { params: { [batchParamKey]: batches[i].join() } }),
        );
        requests.push(request);
      }
    } else {
      const request = this.request<T>('GET', url, configObj);
      requests.push(request);
    }

    return requests;
  }
}
